import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Ruler, Settings as SettingsIcon, UploadCloud, FileText, Trash2, Pencil, MapPinned, Loader2, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import GaugePanel from "@/components/GaugePanel";
import BlnSettingsPanel from "@/components/BlnSettingsPanel";
import KcTest from "@/pages/KcTest";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} minutes ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  const days = Math.floor(s / 86400);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export default function Ballooning() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("ballooning");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingName, setUploadingName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/ballooning");
      setDocs(r.data.documents || []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function uploadFiles(files) {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let firstId = null;
    try {
      for (const f of list) {
        setUploadingName(f.name);
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/ballooning/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (!firstId) firstId = r.data?.document?.id;
      }
      toast.success(list.length > 1 ? `${list.length} drawings uploaded` : "Drawing uploaded");
      if (firstId) { navigate(`/ballooning/${firstId}`); return; }   // open the ballooned page automatically
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadingName("");
    }
  }

  async function renameDoc(d) {
    const name = window.prompt("Rename drawing:", d.name || "");
    if (name == null || !name.trim() || name.trim() === d.name) return;
    try { await api.put(`/ballooning/${d.id}`, { name: name.trim() }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  }
  async function deleteDoc(d) {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try { await api.delete(`/ballooning/${d.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  const TABS = [
    { id: "ballooning", label: "Ballooning", icon: MapPin },
    { id: "gauge", label: "Gauge", icon: Ruler },
    { id: "testbench", label: "Test Bench", icon: FlaskConical },
    { id: "settings", label: "Settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-full bg-slate-50" data-testid="ballooning-page">
      {/* Top bar — AAMPL logo (left) + tabs (centre) */}
      <div className="bg-white border-b border-slate-200 py-3 px-4 flex items-center relative">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white ring-1 ring-slate-200 flex items-center justify-center p-1">
            <img src="/aampl-logo.png" alt="AAMPL" className="w-full h-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-bold text-sm text-slate-800">AAMPL NPD</div>
            <div className="text-[10px] uppercase tracking-wider text-orange-600">Ballooning</div>
          </div>
        </div>
        <div className="absolute left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md font-medium transition ${tab === t.id ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`ballooning-tab-${t.id}`}>
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={`${tab === "ballooning" ? "max-w-3xl" : tab === "testbench" ? "max-w-[1700px]" : "max-w-6xl"} mx-auto px-4 py-10`}>
        {tab === "ballooning" ? (
          <>
            <h1 className="text-3xl font-bold text-slate-700 mb-1">
              Auto-<span className="text-indigo-600">Ballooning</span>
            </h1>
            <p className="text-sm text-slate-500 mb-6">Upload a drawing to auto-balloon dimensions in seconds.</p>

            {/* Dropzone */}
            <div
              onClick={() => { if (!uploading) fileRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!uploading) uploadFiles(e.dataTransfer.files); }}
              className={`border-2 border-dashed rounded-xl px-6 py-10 text-center transition ${uploading ? "border-indigo-300 bg-indigo-50 cursor-wait" : dragOver ? "cursor-pointer border-indigo-400 bg-indigo-50" : "cursor-pointer border-slate-300 bg-white hover:bg-slate-50"}`}
              data-testid="ballooning-dropzone"
            >
              {uploading ? (
                <>
                  <Loader2 size={30} className="mx-auto text-indigo-600 mb-2 animate-spin" />
                  <div className="text-indigo-700 font-semibold">Auto-ballooning your drawing…</div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{uploadingName ? `Reading dimensions from “${uploadingName}”` : "Reading dimensions"} — this can take a few seconds.</div>
                </>
              ) : (
                <>
                  <UploadCloud size={30} className="mx-auto text-indigo-500 mb-2" />
                  <div className="text-slate-600"><span className="text-indigo-600 font-semibold">Click here</span>, or drag and drop.</div>
                  <div className="text-[11px] text-slate-400 mt-1">PDF, JPG, PNG, TIF · max 25 MB</div>
                </>
              )}
              <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.bmp,.webp" className="hidden"
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="ballooning-file-input" />
            </div>

            {/* Recents */}
            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Recents</div>
              {loading ? (
                <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
              ) : docs.length === 0 ? (
                <div className="text-sm text-slate-400 py-8 text-center border border-slate-200 rounded-lg bg-white">No drawings yet. Upload one above to get started.</div>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {docs.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`ballooning-doc-${d.id}`}>
                      <div className="h-12 w-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                        {d.thumb_url ? <img src={d.thumb_url} alt="" className="h-full w-full object-cover" /> : <FileText size={20} className="text-slate-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{d.name}</div>
                        <div className="text-xs text-slate-500">
                          Modified {timeAgo(d.updated_at)}
                          {d.balloon_count > 0 && <span className="ml-2 text-indigo-600 font-medium">{d.balloon_count} dims{d.verified_count ? ` · ${d.verified_count} verified` : ""}</span>}
                        </div>
                      </div>
                      <button onClick={() => renameDoc(d)} className="text-slate-400 hover:text-slate-600 p-1.5" title="Rename"><Pencil size={15} /></button>
                      <button onClick={() => deleteDoc(d)} className="text-slate-400 hover:text-red-600 p-1.5" title="Delete"><Trash2 size={15} /></button>
                      <button onClick={() => navigate(`/ballooning/${d.id}`)}
                        className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-3 py-1.5" data-testid={`ballooning-open-${d.id}`}>
                        <MapPinned size={14} /> Open
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : tab === "gauge" ? (
          <GaugePanel />
        ) : tab === "testbench" ? (
          <KcTest />
        ) : (
          <BlnSettingsPanel />
        )}
      </div>
    </div>
  );
}
